import React from 'react';
import { AppointmentComplianceDto } from '@peartherapeutics/hylia-api-client';
import DatePicker from 'components/common/DatePicker';
import moment from 'moment';
import ButtonMedium from 'components/common/Buttons/ButtonMedium';
import { DATE_FORMATS, shiftDate } from 'utils/dateTime';
import Dialog from '@material-ui/core/Dialog';
import { makeStyles } from '@material-ui/core/styles';
import styles from './styles.module.scss';

export interface IVisitModalProps {
  showVisitModal: boolean;
  selectedAppointmentDate?: Date;
  appointmentCompliances: AppointmentComplianceDto[];
  arStartDate: string;
  arTimezone: string;
  handleCloseModal: ({ isSubmitted }: { isSubmitted: boolean }) => void;
  updateSelectedAppointmentDate: (date: Date) => void;
  updateAppointmentCompliance: () => Promise<void>;
}

const useStyles = makeStyles({
  conatiner: {
    height: 'auto',
    position: 'absolute',
    left: `${window.outerWidth / 2}px`,
    top: `${window.outerHeight / 2 - 80}px`,
    transform: 'translate(-50%, -50%)',
  },
  paper: {
    margin: 0,
  },
});

/**
 * Displays a modal that allows the user to add clinic visits.
 *
 * Dates in this component are in the users timezone because the calendar
 * component deals with Date objects. The dates are offset to reflect the AR
 * timezone.
 */
const VisitModal = (props: IVisitModalProps) => {
  const classes = useStyles();
  const {
    updateAppointmentCompliance,
    showVisitModal,
    selectedAppointmentDate,
    updateSelectedAppointmentDate,
    appointmentCompliances = [],
    arStartDate,
    arTimezone,
  } = props;
  const excludeDates = appointmentCompliances.map((appointment) =>
    shiftDate(new Date(appointment.date), arTimezone),
  );

  const onSubmit = () => {
    if (selectedAppointmentDate) {
      updateAppointmentCompliance();
      props.handleCloseModal({ isSubmitted: true });
    }
  };

  const handleCloseModal = () => {
    props.handleCloseModal({ isSubmitted: false });
  };

  const displaySelectedAppointmentDate = selectedAppointmentDate
    ? moment(selectedAppointmentDate).format(DATE_FORMATS.dddd_MMMM_D)
    : '';

  return (
    <Dialog
      onClose={handleCloseModal}
      open={showVisitModal}
      classes={{ container: classes.conatiner, paper: classes.paper }}
      data-testid="last-clinic-visit-modal"
    >
      <div className={styles['dialog-modal']}>
        <div className={styles['info-section']}>
          <div className={styles['info-message']}>Select Date of Visit</div>
        </div>
        <div>
          <div
            className={styles['access-picker']}
            id="visit-modal"
            data-testid="last-visit-calendar"
          >
            <DatePicker
              minDate={shiftDate(new Date(arStartDate), arTimezone)}
              maxDate={shiftDate(new Date(), arTimezone)}
              excludeDates={excludeDates}
              selected={selectedAppointmentDate ?? null}
              onChange={updateSelectedAppointmentDate}
            />
          </div>
        </div>
        <div className={styles['compliance-date-wrapper']}>
          <span className={styles['compliance-date']}>
            {displaySelectedAppointmentDate}
          </span>
        </div>
        <ButtonMedium
          buttonRole="primary"
          width="100%"
          handleClick={onSubmit}
          disabled={!selectedAppointmentDate}
          data-testid="submit-visit-button"
        >
          SUBMIT
        </ButtonMedium>
        <div
          className={styles['close-btn']}
          onClick={handleCloseModal}
          data-testid="close-modal-button"
        />
      </div>
    </Dialog>
  );
};

export default VisitModal;
